import invariant from "tiny-invariant"
import { id } from "@ethersproject/hash"
import { hexZeroPad } from "@ethersproject/bytes"
import type { BigNumberish } from "@ethersproject/bignumber"
import { useQueryClient } from "@tanstack/react-query"

import { useTimelockOpenZeppelin } from "web3/hooks/useTimelockOpenZeppelin"
import type {
  GovernorContract,
  Governor,
  Proposal,
  AccountId,
} from "query/graphql"
import {
  GovernorType,
  ProposalActionType,
  useCreateProposalActionAttemptMutation,
} from "query/graphql"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import { getAccountAddress } from "web3/helpers/accountId"
import { implementsBravo } from "web3/helpers/validations"
import { useNormalizeProposalId } from "proposal/hooks/useNormalizeProposalId"
import { useCorrectChain } from "web3/hooks/useCorrectChain"
import { useGovernorOpenZeppelin } from "web3/hooks/useGovernorOpenZeppelin"
import { useGovernorAlpha } from "web3/hooks/useGovernorAlpha"
import { useGovernorBravo } from "web3/hooks/useGovernorBravo"
import { useTransaction } from "web3/providers/TransactionProvider"
import { useToast } from "common/hooks/useToast"
import { useChainReference } from "web3/hooks/useChainReference"
import { useSignerStore } from "web3/providers/SignerProvider"
import { useGovernorOpenZeppelinV5 } from "web3/hooks/useGovernorOpenZeppelinV5"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import { useAddress } from "web3/hooks/useAddress"
import { useMe } from "user/providers/MeProvider"
import { useCopyToClipboard } from "common/hooks/useCopyToClipboard"
import { useSafeTransactionV2 } from "safe/hooks/useSafeTransactionV2"

type Governance = Pick<Governor, "id" | "chainId" | "timelockId"> & {
  contracts: {
    governor: Pick<GovernorContract, "type" | "address">
  }
}

type CancelParams = {
  viewTxDetails?: boolean
}

type Values = {
  cancelProposal: (params?: CancelParams) => Promise<void>
  cancelFromTimelock: (params?: CancelParams) => Promise<void>
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type Metadata = {
  proposalId: BigNumberish
  governanceId: AccountId
  governorAddress: string
  type: ProposalActionType
}

type Props = {
  proposal: Proposal
  governance: Governance
}

export const useCancelProposal = ({ proposal, governance }: Props): Values => {
  const { toast } = useToast()
  const address = useAddress()
  const me = useMe()
  const canUseSafeTx = me?.type === "SAFE"
  const { copyToClipboard } = useCopyToClipboard()

  const { isFeatureFlagOn } = useGovernanceContext()
  const isCancelWithMultipleParams = isFeatureFlagOn(
    FeatureFlag.OzGovernorCancelWithMultipleParams,
  )
  const queryClient = useQueryClient()
  const { mutate: createProposalActionAttempt } =
    useCreateProposalActionAttemptMutation({
      onSuccess: (data) => {
        if (data) {
          const queriesToInvalidate = ["ProposalActionAttempt"]
          queriesToInvalidate.forEach((query) =>
            queryClient.invalidateQueries([query]),
          )
        }
      },
    })

  const {
    proposeMultiGnosisTransaction,
    buildSafeTransaction,
    onPendingSafeTx,
  } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: address,
    },
    { enabled: canUseSafeTx },
  )

  const { onchainId, executableCalls, metadata, id: internalId } = proposal

  const calldatas = executableCalls?.map((call) => call.calldata) ?? []
  const targets = executableCalls?.map((call) => call.target) ?? []
  const values = executableCalls?.map((call) => call.value) ?? []

  const { chainId, contracts, id: governanceId, timelockId } = governance
  const { address: governorAddress, type: governorType } = contracts.governor

  const { reference } = getChainIdParams(chainId)
  const chainReferenceName = getChainReferenceName(reference)
  const isOZWithTimelock =
    (governorType === GovernorType.Openzeppelingovernor ||
      governorType === GovernorType.Hub) &&
    Boolean(timelockId)
  const chainReference = useChainReference()
  const { signer } = useSignerStore()
  const { isCorrectChain } = useCorrectChain({ chainId, chainReference })
  const { proposalId } = useNormalizeProposalId({
    proposalId: onchainId ?? "",
    governanceId,
  })

  const timelockAddress = timelockId ? getAccountAddress(timelockId) : undefined

  const governorOz = useGovernorOpenZeppelin(governorAddress, signer)
  const governorOzV5 = useGovernorOpenZeppelinV5(governorAddress, signer)
  const timelockOz = useTimelockOpenZeppelin(timelockAddress, signer)
  const governorAlpha = useGovernorAlpha(governorAddress, signer)
  const governorBravo = useGovernorBravo(governorAddress, signer)

  // TODO(@nicolas): Code disabled due to this existing issue [TALLY-7545]
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const onMining: TransactionOnMining<Metadata> = ({
  //   transaction,
  //   metadata,
  // }) => {
  //   if (address) {
  //     const { hash } = transaction
  //     const { proposalId, type } = metadata

  //     if (type === ProposalActionType.Cancel) {
  //       createProposalActionAttempt({
  //         input: {
  //           actor: address,
  //           proposalId: proposalId as string,
  //           txHash: hash,
  //           type: ProposalActionType.Cancel,
  //         },
  //       })
  //     }
  //   }
  // }

  const handlePendingSafeTx = (values: Record<string, string>) => {
    if (!values.safe || !values.safeTxHash || !values.proposalId || !address) {
      return
    }

    if (values.type === ProposalActionType.Cancel) {
      createProposalActionAttempt({
        input: {
          actor: address,
          proposalId: proposalId as string,
          txHash: values.safeTxHash,
          type: ProposalActionType.Cancel,
        },
      })
    }
  }

  // TODO(@nicolas): Enabling this useTransaction happens that onMining from useCastVote is never executed
  // so the vote attempt is never
  // const { send, setMetadata } = useTransaction({
  //   on: {
  //     [TransactionToastState.Mining]: onMining,
  //   },
  // })
  const { send, setMetadata } = useTransaction()

  const cancelFromTimelock = async (): Promise<void> => {
    if (!isCorrectChain) {
      toast({
        status: "error",
        title: "Incorrect network",
        description: `Connect to ${chainReferenceName} to cancel this proposal`,
        duration: 10000,
      })

      return
    }

    if (!executableCalls) {
      toast({
        status: "error",
        title: "Unable to cancel proposal",
        description: "There are no executable calls found",
        duration: 10000,
      })

      return
    }

    const descriptionHash = id(metadata.description)

    const paddedPredecessor = hexZeroPad("0x00", 32)

    const proposalTimelockId = await timelockOz?.hashOperationBatch(
      targets,
      values,
      calldatas,
      paddedPredecessor,
      descriptionHash,
    )

    if (proposalTimelockId) {
      invariant(timelockOz, "governorOz factory is not defined")
      send(() => timelockOz?.cancel(proposalTimelockId))
    }
  }

  const cancelProposal = async (params?: CancelParams): Promise<void> => {
    if (!isCorrectChain) {
      toast({
        status: "error",
        title: "Incorrect network",
        description: `Connect to ${chainReferenceName} to cancel this proposal`,
        duration: 10000,
      })

      return
    }

    if (!proposalId) {
      toast({
        status: "error",
        title: "Cannot find proposal id",
        description: "Unable to find proposal id",
        duration: 10000,
      })

      return
    }

    switch (governorType) {
      case GovernorType.Governoralpha:
        invariant(governorAlpha, "governorAlpha factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Cancel,
        })

        if (canUseSafeTx) {
          const safeTxHash = await proposeMultiGnosisTransaction([
            buildSafeTransaction(
              governorAlpha,
              "cancel",
              [proposalId],
              governorAddress,
            ),
          ])

          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: ["proposalId"],
            expectedMethod: "cancel",
          })((values) =>
            handlePendingSafeTx({
              ...values,
              type: ProposalActionType.Cancel,
              proposalId: internalId,
            }),
          )
        } else {
          send(() => governorAlpha.cancel(proposalId))
        }

        break

      case GovernorType.Nounsfork:
      case GovernorType.Governorbravo:
        invariant(governorBravo, "governorBravo factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Cancel,
        })

        if (canUseSafeTx) {
          const safeTxHash = await proposeMultiGnosisTransaction([
            buildSafeTransaction(
              governorBravo,
              "cancel",
              [proposalId],
              governorAddress,
            ),
          ])

          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: ["proposalId"],
            expectedMethod: "cancel",
          })((values) =>
            handlePendingSafeTx({
              ...values,
              type: ProposalActionType.Cancel,
              proposalId: internalId,
            }),
          )
        } else {
          send(() => governorBravo.cancel(proposalId))
        }

        break

      case GovernorType.Hub:
      case GovernorType.Openzeppelingovernor:
        invariant(governorOz, "governorOz factory is not defined")
        invariant(governorOzV5, "governorOzV5 factory is not defined")

        setMetadata({
          proposalId: internalId,
          governanceId,
          governorAddress,
          type: ProposalActionType.Cancel,
        })

        const doesImplementBravo = await implementsBravo(governorOz)

        if (!isOZWithTimelock || !doesImplementBravo) return

        // Check out if cancel function receives multiple params (starting on OZ Govenror v4.9)
        if (isCancelWithMultipleParams) {
          const descriptionHash = id(metadata.description)

          if (canUseSafeTx) {
            if (params?.viewTxDetails) {
              const txDetails = `
                to address: ${governorAddress}
                ------------------------------
                method: cancel
                ------------------------------
                targets: [${targets}]
                values: [${values}]
                calldatas: [${calldatas}]
                descriptionHash: ${descriptionHash}
              `

              copyToClipboard(txDetails)

              alert("Transaction details copied to clipboard")

              return
            }

            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorOzV5,
                "cancel",
                [targets, values, calldatas, descriptionHash],
                governorAddress,
              ),
            ])

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: [
                "targets",
                "values",
                "calldatas",
                "descriptionHash",
              ],
              expectedMethod: "cancel",
            })((values) =>
              handlePendingSafeTx({
                ...values,
                type: ProposalActionType.Cancel,
                proposalId: internalId,
              }),
            )
          } else {
            send(() =>
              governorOzV5.cancel(targets, values, calldatas, descriptionHash),
            )
          }
        }

        // Otherwise, use the old cancel function (only proposalId is required)
        else {
          if (canUseSafeTx) {
            const safeTxHash = await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorOz,
                "cancel",
                [proposalId],
                governorAddress,
              ),
            ])

            if (params?.viewTxDetails) {
              const txDetails = `
                to address: ${governorAddress}
                ------------------------------------
                contract method: cancel
                ------------------------------------
                proposalId: ${proposalId}
              `

              copyToClipboard(txDetails)

              alert("Transaction details copied to clipboard")

              return
            }

            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: ["proposalId"],
              expectedMethod: "cancel",
            })((values) =>
              handlePendingSafeTx({
                ...values,
                type: ProposalActionType.Cancel,
                proposalId: internalId,
              }),
            )
          } else {
            send(() => governorOz.cancel(proposalId))
          }
        }

        break
      default:
        break
    }
  }

  return { cancelProposal, cancelFromTimelock }
}
